import { Fragment } from 'react'
import Icon from './Icon'
import Chip from './Chip'
import styles from './InsightComparisonBlock.module.css'

/**
 * InsightComparisonBlock — renders a `comparison` content block: the
 * Today-vs-Recommended two-column execution model, with a center connector
 * arrow and a color legend, enclosed in its own gradient card (including its
 * own heading — BlockRenderer skips the generic heading render for this
 * type and lets this component own that placement). Split out from
 * BlockRenderer's other (smaller) renderers because this is the most
 * bespoke, CSS-heavy layout in the schema — bundling it in would bloat that
 * file disproportionately.
 *
 * Props mirror the `comparison` block schema:
 *   heading     {string} — the block's eyebrow (e.g. "Recommended Model")
 *   modelLabel  {string}
 *   legend      {Array<{label: string, color: string}>}
 *   today       {{pillLabel, subLabel, body, footerBadge?}}
 *   recommended {{pillLabel, subLabel, body, guardrails?: string[]}} —
 *               guardrails renders as a "Human Approval Stays On" sub-section
 *               nested at the bottom of this column, per the design.
 */
export default function InsightComparisonBlock({ heading, modelLabel, legend = [], today, recommended }) {
  return (
    <div className={styles.wrap}>
      <div className={styles.top}>
        <div>
          {heading && <div className={styles.heading}>{heading}</div>}
          <span className={styles.modelLabel}>{modelLabel}</span>
        </div>
        {legend.length > 0 && (
          <div className={styles.legend}>
            {legend.map((item, i) => (
              <Fragment key={item.label}>
                {i > 0 && <span className={styles.legendArrow} aria-hidden="true">→</span>}
                <span className={styles.legendItem}>
                  <span className={styles.legendDot} style={{ backgroundColor: item.color }} />
                  {item.label}
                </span>
              </Fragment>
            ))}
          </div>
        )}
      </div>

      <div className={styles.columns}>
        <div className={styles.column}>
          <div className={styles.subHeadRow}>
            <span className={`${styles.pill} ${styles.pillToday}`}>{today.pillLabel}</span>
            <span className={styles.subLabel}>{today.subLabel}</span>
          </div>
          <p className={styles.body}>{today.body}</p>
          {today.footerBadge && (
            <div className={styles.footerRow}>
              <span className={styles.footerBadge}>{today.footerBadge}</span>
              <span className={styles.footerRule} />
            </div>
          )}
        </div>

        <div className={styles.connectorCol}>
          <div className={styles.connector} aria-hidden="true">
            <Icon name="arrow_right" />
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.subHeadRow}>
            <span className={`${styles.pill} ${styles.pillRecommended}`}>{recommended.pillLabel}</span>
            <span className={styles.subLabel}>{recommended.subLabel}</span>
          </div>
          <p className={styles.body}>{recommended.body}</p>

          {recommended.guardrails?.length > 0 && (
            <div className={styles.guardrails}>
              <span className={styles.guardrailsRule} />
              <span className={styles.guardrailsHeading}>Human Approval Stays On</span>
              <div className={styles.guardrailsChips}>
                {recommended.guardrails.map(label => <Chip key={label} label={label} tone="orange" />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
